import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpec } from '../../contexts/SpecContext';
import type { TestListItem } from '../../types/test-types';
import { getStatusLabel, getStatusTooltip } from '../../utils/testStatusLabel';
import styles from './TestExecution.module.css';

interface TestsListProps {
  tests: TestListItem[];
  loading: boolean;
  formatDate: (dateString: string) => string;
  selectedTests: Set<string>;
  onToggleTest: (filename: string) => void;
  onToggleSelectAll: () => void;
  disabled: boolean;
}

export const TestsList: React.FC<TestsListProps> = ({
  tests,
  loading,
  formatDate,
  selectedTests,
  onToggleTest,
  onToggleSelectAll,
  disabled,
}) => {
  const navigate = useNavigate();
  const { activeSpec } = useSpec();

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div>Loading tests...</div>
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📋</div>
        <p className={styles.emptyText}>No tests found. Generate hypotheses first to create tests.</p>
      </div>
    );
  }

  const handleCardClick = (test: TestListItem) => {
    if (activeSpec?.id && test.filename) {
      navigate(`/tests/${activeSpec.id}/${test.filename}`);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, filename: string) => {
    e.stopPropagation();
    onToggleTest(filename);
  };

  const allSelected = tests.length > 0 && selectedTests.size === tests.length;

  return (
    <div className={styles.testsList}>
      <div className={styles.selectAllRow}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleSelectAll}
            disabled={disabled}
            className={styles.testCheckbox}
          />
          <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
        </label>
      </div>
      {tests.map((test, index) => (
        <div key={index} className={styles.testCard} onClick={() => handleCardClick(test)}>
          <label className={styles.checkboxHitArea} onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedTests.has(test.filename)}
              onChange={(e) => handleCheckboxChange(e, test.filename)}
              disabled={disabled}
              className={styles.testCheckbox}
            />
          </label>
          <div className={styles.testIcon}>🧪</div>
          <div className={styles.testInfo}>
            <div className={styles.testName}>{test.filename}</div>
            <div className={styles.testMeta}>
              <span>{test.strategy}</span>
              {getStatusLabel(test.status) !== null && (
                <>
                  <span>•</span>
                  <span
                    className={styles.statusBadge}
                    data-status={test.status}
                    title={getStatusTooltip(test.status) ?? undefined}
                  >
                    {getStatusLabel(test.status)}
                  </span>
                </>
              )}
              {test.lastRun && (
                <>
                  <span>•</span>
                  <span className={styles.lastRun}>Last run: {formatDate(test.lastRun)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
